import * as React from 'react';
import { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { IHasTestData } from '../../test-utilities/IHasTestData';
import { inputColorMixin } from '../TextInput';
import { mergeTests } from '../../test-utilities/mergeTests';

export interface IDropdownItemLinkProps {
  displayAsGroup?: boolean | null;
  isSelectable?: boolean;
  selected?: boolean;
}

const dropdownItemCssFunction = css<IDropdownItemLinkProps>`
  ${inputColorMixin}
  ${(props) => props.theme.fonts.mixins.smallButton};
  align-items: center;
  border-color: ${(props) => (!props.displayAsGroup ? props.theme.colors.activeControlBackground : 'transparent')};
  color: ${(props) => props.theme.colors.darkGray};
  cursor: not-allowed;
  display: grid;
  gap: ${(props) => props.theme.spacing.spacing5};
  ${(props) => (props.isSelectable ? 'grid-template-columns: 14px 1fr;' : 'grid-auto-flow: column;')};
  outline: none;
  padding: ${(props) => `${props.displayAsGroup ? props.theme.spacing.spacing2 : ''} ${props.theme.spacing.spacing4}`};
  text-decoration: none;
  text-transform: none;
  width: 100%;
`;

const dropdownItemCssInteractiveFunction = css<IDropdownItemLinkProps>`
  color: ${(props) => props.theme.colors.gray700};
  cursor: pointer;
  :hover,
  :focus,
  :active {
    background-color: ${(props) => props.theme.colors.activeControlBackground};
  }
`;

const DropdownItemLink = styled.a<IDropdownItemLinkProps>`
  ${dropdownItemCssFunction}
  ${dropdownItemCssInteractiveFunction}
`;
const DropdownItemInternalLink = styled(Link)<IDropdownItemLinkProps>`
  ${dropdownItemCssFunction}
  ${dropdownItemCssInteractiveFunction}
`;

const DropdownItemButton = styled.button<IDropdownItemLinkProps>`
  ${dropdownItemCssFunction}
  ${dropdownItemCssInteractiveFunction}
`;

const DropdownItemText = styled.div<IDropdownItemLinkProps>`
  ${dropdownItemCssFunction}
`;

//FYI: this extra div is here because button with display:grid does not currently work in Google Chrome
export const StyledDropdownItemContent = styled.div`
  align-items: center;
  display: grid;
  gap: ${(props) => props.theme.spacing.spacing4};
  grid-auto-flow: column;
  grid-auto-columns: max-content;
`;

export interface IDropdownItemProps extends IDropdownItemLinkProps, IHasTestData {
  children?: ReactNode;
  to?: string;
  href?: string;
  onClick?: (href?: string | null) => void;
}

//FYI: this craziness is due to us wanting to display anchor vs button and their incompatible props in typescript
const InteractiveDropdownItem: React.FC<IDropdownItemProps> = ({ children, onClick, ...rest }) => {
  const onWrappedClick = (event: React.MouseEvent) => {
    if (onClick) {
      event.preventDefault();
      event.stopPropagation();
      onClick(rest.href || rest.to);
    }
  };
  return rest.href ? (
    <DropdownItemLink {...rest} onClick={onWrappedClick}>
      {rest.isSelectable && (rest.selected ? <FontAwesomeIcon icon={faCheck} /> : <span />)}
      {children}
    </DropdownItemLink>
  ) : onClick ? (
    <DropdownItemButton {...rest} type={'button'} onClick={onWrappedClick}>
      {rest.isSelectable && (rest.selected ? <FontAwesomeIcon icon={faCheck} /> : <span />)}
      {children}
    </DropdownItemButton>
  ) : rest.to ? (
    <DropdownItemInternalLink {...rest} to={rest.to} onClick={onWrappedClick}>
      {rest.isSelectable && (rest.selected ? <FontAwesomeIcon icon={faCheck} /> : <span />)}
      {children}
    </DropdownItemInternalLink>
  ) : (
    <DropdownItemText {...rest}>
      {rest.isSelectable && (rest.selected ? <FontAwesomeIcon icon={faCheck} /> : <span />)}
      {children}
    </DropdownItemText>
  );
};

export const DropdownItem: React.FC<IDropdownItemProps> = ({ children, ...rest }) => (
  <InteractiveDropdownItem {...rest}>
    <StyledDropdownItemContent
      data-test-class={'drop-down-item'}
      data-test-id={mergeTests(rest['data-test-id'], 'drop-down-item', typeof children === 'string' ? children : '')}
    >
      {children}
    </StyledDropdownItemContent>
  </InteractiveDropdownItem>
);
